import React, { ComponentProps, createContext, useEffect, useRef, useState } from "react"
import * as RAccordion from "@radix-ui/react-accordion"
import clsx from "clsx"
import { twMerge } from "tailwind-merge"
import { useMedia } from "react-use"
import { tv } from "tailwind-variants"
import { IconCaretDown } from "@components/svg/Icons"

type Props = RAccordion.AccordionSingleProps & {
  options?: { label: React.ReactNode; body: React.ReactNode; value: string }[]
}

export function Accordion(props: Props) {
  const { options, className, ...accordionProps } = props

  return (
    <RAccordion.Root data-transition="children/fade" {...accordionProps} className={clsx("w-full", className)} collapsible>
      {options
        ? options.map(({ label, body, value }) => (
            <AccordionItem key={value} value={value}>
              <AccordionTrigger>{label}</AccordionTrigger>
              <AccordionContent>{body}</AccordionContent>
            </AccordionItem>
          ))
        : props.children}
    </RAccordion.Root>
  )
}

export const AccordionItem = React.forwardRef<React.ElementRef<typeof RAccordion.Item>, React.ComponentPropsWithoutRef<typeof RAccordion.Item>>(
  ({ className, children, ...props }, ref) => (
    <RAccordion.Item className={clsx("overflow-hidden my-2", className)} {...props} ref={ref}>
      {children}
    </RAccordion.Item>
  )
)

export const AccordionTrigger = React.forwardRef<React.ElementRef<typeof RAccordion.Trigger>, React.ComponentPropsWithoutRef<typeof RAccordion.Trigger>>(
  ({ className, children, ...props }, ref) => (
    <RAccordion.Header className="flex">
      <RAccordion.Trigger className={clsx("flex items-center py-1 type-title-l-medium group", className)} {...props} ref={ref}>
        <span className="relative flex mr-3 accordion-content-indicator" aria-hidden>
          <span className="group-data-[state=open]:opacity-0">+</span>
          <span className="opacity-0 group-data-[state=open]:opacity-100 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">–</span>
        </span>
        {children}
      </RAccordion.Trigger>
    </RAccordion.Header>
  )
)

export const AccordionContent = React.forwardRef<React.ElementRef<typeof RAccordion.Content>, React.ComponentPropsWithoutRef<typeof RAccordion.Content>>(
  ({ className, children, ...props }, ref) => (
    <RAccordion.Content
      className={clsx("data-[state=open]:animate-accordionSlideDown data-[state=closed]:animate-accordionSlideUp overflow-hidden", className)}
      {...props}
      ref={ref}
    >
      <div className="accordion-content-inner flex mt-2">
        {/* for spacing */}
        <span className="accordion-content-indicator relative flex w-button group invisible" aria-hidden>
          +
        </span>

        <div className="type-body-m-serif w-full py-2">{children}</div>
      </div>
    </RAccordion.Content>
  )
)

const mobileAccordionStyles = tv({
  slots: {
    base: "flex flex-col",
    trigger: "type-title-xs-medium pb-3 border-b border-black flex gap-2 items-center justify-between text-left el-svg:transition-transform sm:el-svg:hidden sm:cursor-default",
    wrapper: "auto-accordion",
    content: "",
  },
  variants: {
    open: {
      true: {
        trigger: "el-svg:rotate-180",
        wrapper: "is-open",
      },
      false: {
        wrapper: "is-closed sm:is-open",
      },
    },
  },
})

export function MobileAccordion(props: { header: React.ReactNode; children: React.ReactNode }) {
  const [open, setOpen] = useState(false)

  const styles = mobileAccordionStyles({ open })

  return (
    <div className={styles.base()}>
      <button type="button" className={styles.trigger()} onClick={() => setOpen(!open)}>
        <div>{props.header}</div>
        <IconCaretDown />
      </button>
      <div className={styles.wrapper()}>
        <div className={styles.content()}>{props.children}</div>
      </div>
    </div>
  )
}
